import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  useTheme,
  Checkbox,
} from '@mui/material';
import { PdfDocument } from '../../Document/PdfDocument';
import type { IDocument } from '../../Interfaces/IDocument';
import { useMemo, useState } from 'react';
import { DocumentDialog } from './DocumentDialog';
import { useGetDocument } from '../../hooks/reactQuerys/DocumentQueries';
import {
  decodeTextDocument,
  documentPreviewKind,
} from '../../services/documentPreview.service';

type IProps = {
  initialDocument: IDocument;
  selectable?: boolean;
  selected?: boolean;
  onSelectChange?: (selected: boolean) => void;
};

const getFileExtension = (fileName?: string): string => {
  if (typeof fileName !== 'string') return '';
  const parts = fileName.toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() || '' : '';
};

const getImageMimeType = (fileName?: string): string => {
  const ext = getFileExtension(fileName);

  switch (ext) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    case 'bmp':
      return 'image/bmp';
    case 'svg':
      return 'image/svg+xml';
    default:
      return 'image/*';
  }
};

export const DocumentCard = ({
  initialDocument,
  selectable = false,
  selected = false,
  onSelectChange,
}: IProps) => {
  const theme = useTheme();

  const { document, isLoadingDocument } = useGetDocument({
    idDocument: initialDocument.idDocument,
    enabled: !initialDocument.Data,
  });

  const [showDocumentDialog, setShowDocumentDialog] = useState(false);

  const actualDocument = document ?? initialDocument;

  // Eine Entscheidung fuer alle Zweige: Dateiendung, und wenn die nichts
  // hergibt, der Dateityp aus der Datenbank. Im Ergebnis-Dialog der
  // Nachverfolgung heisst das Platzhalter-Dokument "Dokument 18611" — ohne
  // Endung galt ein PDF dort als "keine Vorschau" (QS #24).
  const previewKind = useMemo(
    () =>
      documentPreviewKind(actualDocument.FileName, actualDocument.idMIMEType),
    [actualDocument.FileName, actualDocument.idMIMEType]
  );
  const isPdf = previewKind === 'pdf';
  const isImage = previewKind === 'image';

  const imageSrc = useMemo(() => {
    if (!actualDocument.Data || !isImage) return null;
    return `data:${getImageMimeType(actualDocument.FileName)};base64,${actualDocument.Data}`;
  }, [actualDocument.Data, actualDocument.FileName, isImage]);

  // QS #24: Textformate — vor allem die XML-Inhaltsdatei (SI) des
  // Handelsregisters — landeten unter "Keine Vorschau". Sie lassen sich im
  // Klartext zeigen.
  const textPreview = useMemo(() => {
    if (previewKind !== 'text') return null;
    return decodeTextDocument(actualDocument.Data);
  }, [actualDocument.Data, previewKind]);

  if (!isLoadingDocument && !actualDocument.Data) {
    return (
      <Card variant='outlined' sx={{ height: '100%' }}>
        <CardContent
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Typography variant='body2' color='error'>
            Dokument konnte nicht geladen werden
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const handleCardClick = () => {
    setShowDocumentDialog(true);
  };

  const handleCheckboxClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onSelectChange?.(!selected);
  };

  const renderPreview = () => {
    if (!actualDocument.Data) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress size={20} />
        </Box>
      );
    }

    if (isPdf) {
      return (
        <PdfDocument
          pdfFile={`data:application/pdf;base64,${actualDocument.Data}`}
          numPages={1}
          pageNumber={1}
          scale={0.3}
          setNumPages={() => {}}
        />
      );
    }

    if (textPreview !== null) {
      return (
        <Box
          component='pre'
          sx={{
            m: 0,
            p: 1,
            height: '100%',
            overflow: 'auto',
            fontSize: 9,
            lineHeight: 1.3,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            fontFamily: 'monospace',
          }}
        >
          {textPreview}
        </Box>
      );
    }

    if (isImage && imageSrc) {
      return (
        <Box
          component='img'
          src={imageSrc}
          alt={actualDocument.FileName || 'Dokumentbild'}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      );
    }

    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: 1,
          textAlign: 'center',
        }}
      >
        <Typography variant='body2' fontWeight={600}>
          Keine Vorschau
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          Datei kann trotzdem ausgewählt und versendet werden
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ position: 'relative' }} onClick={handleCardClick}>
        <Card
          variant='outlined'
          sx={{
            cursor: 'pointer',
            '&:hover': {
              border: `1px solid ${theme.palette.primary.main}`,
              transition: 'border 0.3s ease-in-out',
            },
          }}
        >
          <CardContent sx={{ p: 1 }}>
            <Typography variant='body2' noWrap title={actualDocument.FileName}>
              {actualDocument.FileName}
            </Typography>

            <Box
              sx={{
                height: '100px',
                overflow: 'hidden',
                mt: 1,
              }}
            >
              {renderPreview()}
            </Box>
          </CardContent>
        </Card>

        {selectable && (
          <Checkbox
            checked={selected}
            onClick={handleCheckboxClick}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(255,255,255,0.8)',
            }}
          />
        )}
      </Box>

      <DocumentDialog
        document={actualDocument}
        open={showDocumentDialog}
        onClose={() => setShowDocumentDialog(false)}
      />
    </>
  );
};
